import { action, observable, makeObservable, reaction } from 'mobx';
import { AxiosError } from 'axios';

import { arrayMove } from '@dnd-kit/sortable';

import { Active, Over, UniqueIdentifier } from '@dnd-kit/core';

import _, { set, isEqual } from 'lodash';

import { v4 as uuid } from 'uuid';

import DataMixin from 'utils/stores/DataMixin';

import API from 'utils/api';
import RootStore from 'stores/Root';
import { Items } from 'containers/LayoutEditor/components/Layout/types';

import { EntityLayoutType, EntityPropertiesType, LockedField } from 'utils/api/modules/entities/entities.types';
import Logger from 'utils/logger';

import { FixedSection, getFixedSection } from './fixedSections';

const UNTITLED_SECTION_PREFIX = 'untitledSection';
export const FIELD_CONTAINER_ID = 'fields_container';

type InitialContainerInfo = { container: string | null; index: number };

export default class EntityLayoutStore extends DataMixin {
  @observable error?: AxiosError;

  @observable data?: EntityLayoutType;

  // data loaded at first, needed to compare with the current data
  @observable loadedData?: EntityLayoutType;

  @observable lastPublished?: string;

  @observable containers: string[] = [];

  @observable items: Items = {};

  @observable contentOfLayoutWasEdited = false;

  @observable activeId: UniqueIdentifier | null = null;

  @observable clonedItems: Items | null = null;

  @observable initialContainerInfo: InitialContainerInfo = { container: null, index: 0 };

  // Set from the module being edited: the section the customer cannot touch, if this object has one.
  @observable fixedSection?: FixedSection;

  // Fields that must stay somewhere in the layout though their value is optional, by key.
  @observable lockedFields: Record<string, LockedField> = {};

  constructor(rootStore: RootStore, api: typeof API) {
    super(rootStore, api);
    makeObservable(this);

    // Reaction to observe changes
    reaction(
      () => [this.data, this.items, this.containers],
      () => {
        this.isModified();
      }
    );
  }

  @action.bound
  async loadLayout(entityType: string): Promise<void> {
    this.isLoaded = false;
    this.fixedSection = getFixedSection(entityType);
    this.lockedFields = {};
    try {
      const {
        data: { layout, createdAt, lockedFields },
      } = await this.api.entities.loadEntityLayout(entityType)();
      this.lockedFields = lockedFields ?? {};
      const normalised = this.normaliseLockedFields(this.normaliseFixedSection(layout));
      this.data = normalised;
      // The layout as received, not the normalised one: when normalising changed something the
      // editor is showing a correction the server does not have yet, and Publish must be enabled.
      this.loadedData = layout;
      this.lastPublished = createdAt;
      this.containers = normalised.order;
      this.transformDataForDisplay();
      const fieldsIds = Object.values(this.items).flat();
      this.store.entityFields.changeFieldsToDisabled(fieldsIds as string[]);
      this.error = undefined;
    } catch (err) {
      const e = err as AxiosError;
      this.error = e;
    } finally {
      this.isLoaded = true;
    }
  }

  isFixedSection = (key?: string | null): boolean => Boolean(key) && this.fixedSection?.key === key;

  // A saved layout may predate the rule, so render the fixed section from its definition and
  // return any stray it held to the first section.
  normaliseFixedSection = (layout: EntityLayoutType): EntityLayoutType => {
    const fixed = this.fixedSection;
    if (!fixed) return layout;

    const fixedFields = new Set(fixed.fieldKeys);
    // The API treats a section listed in `order` with nothing under `properties` as a valid shape,
    // so drop it rather than throw on it. It holds no fields, so nothing is lost.
    const order = layout.order.filter((key) => key !== fixed.key && layout.properties[key]);
    const properties = { ...layout.properties };
    const strays = (properties[fixed.key]?.order || []).filter((key) => !fixedFields.has(key));

    order.forEach((key) => {
      properties[key] = { ...properties[key], order: properties[key].order.filter((f) => !fixedFields.has(f)) };
    });

    if (order.length > 0) {
      const [first] = order;
      properties[first] = { ...properties[first], order: [...properties[first].order, ...strays] };
    }

    // Spread, never rebuild: the server's sections carry no `type` and an editor-saved one carries
    // no `description`, and forcing either would leave the layout permanently dirty on open.
    properties[fixed.key] = {
      ...properties[fixed.key],
      order: [...fixed.fieldKeys],
      title: fixed.title,
    };

    return { ...layout, order: [...order, fixed.key], properties };
  };

  lockedField = (key: string): LockedField | undefined => this.lockedFields[key];

  mustStayInLayout = (key: string): boolean =>
    this.store.entityFields.checkIfFieldIsRequired(key) || Boolean(this.lockedFields[key]);

  normaliseLockedFields = (layout: EntityLayoutType): EntityLayoutType => {
    const placed = new Set(layout.order.flatMap((key) => layout.properties[key]?.order ?? []));
    const missing = Object.keys(this.lockedFields).filter((key) => !placed.has(key));
    const destination = layout.order.find((key) => !this.isFixedSection(key));
    if (missing.length === 0 || !destination) return layout;

    const section = layout.properties[destination];
    return {
      ...layout,
      properties: { ...layout.properties, [destination]: { ...section, order: [...section.order, ...missing] } },
    };
  };

  // Every writer of `containers` goes through this: the fixed section is last or the save is rejected.
  withFixedSectionLast = (order: string[]): string[] => {
    if (!this.fixedSection || !order.includes(this.fixedSection.key)) return order;

    const { key } = this.fixedSection;
    return [...order.filter((section) => section !== key), key];
  };

  findItem = (id: string): string | undefined => {
    if (id in this.items) {
      return id;
    }
    return Object.keys(this.items).find((key) => this.items[key].includes(id));
  };

  @action.bound
  transformDataForDisplay(): void {
    if (!this.data) return;
    const result: Items = {};

    this.data.order.forEach((key: string) => {
      result[key] = this.data!.properties[key].order;
    });
    this.items = result;
  }

  getNextContainerId = (): string => {
    const value = uuid();
    return `${UNTITLED_SECTION_PREFIX}${value}`;
  };

  @action.bound
  addSection(customTitle?: string): string {
    if (!this.data) return '';

    const key = this.getNextContainerId();
    this.containers = this.withFixedSectionLast([...this.containers, key]);

    const properties = _.mapValues(this.items, (value, k) => ({
      type: 'object',
      order: value,
      title: this.getSectionTitle(k),
      description: '',
    }));

    this.data = {
      ...this.data,
      order: this.containers,
      // @ts-ignore
      properties: {
        ...properties,
        [key]: {
          type: 'object',
          order: [],
          title: customTitle || 'Untitled section',
          description: 'Key Fields',
        },
      },
    };

    this.transformDataForDisplay();
    return key;
  }

  removeSectionFromLayout = (key: string): void => {
    if (!this.data || this.isFixedSection(key)) return;
    const section = this.items[key];
    const requiredFields = section.filter((field) => this.mustStayInLayout(field as string));

    if (requiredFields.length === 0) {
      // if section does not contain required fields, add all fields must be added to the fields list
      this.store.entityFields.addAllFieldsFromRemovedContainer(this.items[key] as string[]);
      this.removeSection(key);
      return;
    }

    // any other fields in the section
    const otherFields = section.filter((field) => !requiredFields.includes(field as string));
    this.store.entityFields.addAllFieldsFromRemovedContainer(otherFields as string[]);

    this.removeSection(key);

    // The first section that can take them: a fixed one rejects anything but its own two fields.
    const destination = this.containers.find((container) => !this.isFixedSection(container));

    if (destination) {
      // add fields to that section but do not override existing fields
      const existingFields = this.items[destination] as string[];
      const newFields = requiredFields.filter((field) => !existingFields.includes(field as string)) as string[];
      this.modifyItemsInContainer(destination, [...existingFields, ...newFields]);
    } else {
      const uncategorised = this.addSection('Uncategorised');
      this.modifyItemsInContainer(uncategorised, requiredFields as string[]);
    }

    this.store.notification.enqueueWarningSnackbar(
      'The section has been removed. Required fields have been moved to the first section.'
    );
  };

  @action.bound
  removeSection(key: string): void {
    this.containers = this.containers.filter((container) => container !== key);
  }

  @action.bound
  reorderSections(active: string, over: string): void {
    // Blocked on `over` too: dropping another section onto the last one would push the fixed one up.
    if (this.isFixedSection(active) || this.isFixedSection(over)) return;

    const activeIndex = this.containers.indexOf(active);
    const overIndex = this.containers.indexOf(over);

    // `over` is often a field row or the Fields panel, not a section, and arrayMove reads the
    // resulting -1 as "move to the end" — past a fixed section.
    if (activeIndex === -1 || overIndex === -1) return;

    this.containers = arrayMove(this.containers, activeIndex, overIndex);
  }

  @action.bound
  getSectionTitle(key: string): string {
    if (!this.data) return '';
    return this.data.properties[key].title || 'N/A';
  }

  // items
  @action.bound
  addFieldToContainer(activeContainer: string, newContainer: string, id: string): void {
    this.items = {
      ...this.items,
      [activeContainer]: this.items[activeContainer].filter((field) => field !== id),
      [newContainer]: [...this.items[newContainer], id],
    };
  }

  @action.bound
  modifyItemsInContainer(container: string, items: string[]): void {
    this.items = {
      ...this.items,
      [container]: items,
    };
  }

  @action.bound
  moveItemsBetweenContainers(
    activeContainer: string,
    overContainer: string,
    overId: string,
    active: Active,
    over: Over
  ): void {
    const activeItems = this.items[activeContainer];
    const overItems = this.items[overContainer];
    const activeIndex = activeItems.indexOf(active.id);
    if (!active.rect.current.translated) return;
    const newIndex =
      overId in this.items
        ? overItems.length
        : overItems.indexOf(overId) + (active.rect.current.translated.top > over.rect.top + over.rect.height ? 1 : 0);

    this.items = {
      ...this.items,
      [activeContainer]: activeItems.filter((item) => item !== active.id),
      [overContainer]: [...overItems.slice(0, newIndex), activeItems[activeIndex], ...overItems.slice(newIndex)],
    };
  }

  @action.bound
  setItems(items: Items): void {
    this.items = items;
  }

  @action.bound
  renameSection(sectionId: string, newTitle: string): void {
    if (!this.data) return;
    this.data = set({ ...this.data }, `properties.${sectionId}.title`, newTitle);
  }

  // check if the layout has been modified
  @action.bound
  isModified(): void {
    if (this.loadedData === undefined) return;

    const containersAreDifferent = !isEqual(this.loadedData.order, this.containers);

    const dataAreDifferent = !isEqual(this.data?.properties, this.loadedData?.properties);

    let propertiesAreDifferent = false;
    Object.keys(this.loadedData.properties).forEach((key) => {
      if (key === 'description' || key === 'title') return;
      if (!isEqual(this.loadedData?.properties[key].order, this.items[key])) {
        propertiesAreDifferent = true;
      }
    });

    this.contentOfLayoutWasEdited = containersAreDifferent || propertiesAreDifferent || dataAreDifferent;
  }

  derivedSectionKey = (container: string): string =>
    container.startsWith(UNTITLED_SECTION_PREFIX) ? _.camelCase(this.getSectionTitle(container)) : container;

  // SAVING
  @action.bound
  async saveLayout(entityType: string): Promise<void> {
    // check if there is no empty section
    const emptySections = this.containers.filter((container) => this.items[container].length === 0);
    if (emptySections.length > 0) {
      this.store.notification.enqueueErrorSnackbar('Sections cannot be empty');
      return;
    }

    const sectionTitles = this.containers.map((container) => this.getSectionTitle(container));
    if (new Set(sectionTitles).size !== sectionTitles.length) {
      this.store.notification.enqueueErrorSnackbar('Section names must be unique');
      return;
    }

    // Distinct titles can still camel-case onto one key in transformDataForSave — "Risk scoring"
    // onto an existing riskScoring — so this is a clash, not a duplicate name.
    const sectionKeys = this.containers.map((container) => this.derivedSectionKey(container));
    if (new Set(sectionKeys).size !== sectionKeys.length) {
      this.store.notification.enqueueErrorSnackbar(
        'This section name is too close to an existing one. Please choose a different name.'
      );
      return;
    }
    try {
      const updated = await this.api.entities.updateEntityLayout(entityType, this.transformDataForSave())();
      this.contentOfLayoutWasEdited = false;
      this.loadedData = updated.data.layout;
      this.lastPublished = updated.data.createdAt;
      this.store.notification.enqueueSuccessSnackbar('Layout published successfully');
    } catch (err) {
      const e = err as AxiosError<{ layout?: string[] }>;

      // Backend returns a structured `layout` array for known business validations
      // (e.g. removing a field still referenced as a pipeline relevant field).
      // Surface those as a toast only — they aren't unexpected errors and don't belong in Sentry.
      const validationMessage = e.response?.data?.layout?.join(', ');
      const errorMessage = validationMessage || 'Could not save layout';
      this.store.notification.enqueueErrorSnackbar(errorMessage);
      if (!validationMessage) {
        Logger.error(errorMessage);
      }
    }
  }

  // DRAG HANDLERS
  @action.bound
  onDragStart(active: Active): void {
    this.activeId = active.id;
    this.clonedItems = this.items;
    const activeContainer = this.findItem(active.id as string);
    if (!activeContainer || !this.items[activeContainer]) {
      this.initialContainerInfo = { container: null, index: 0 };
      return;
    }
    this.initialContainerInfo = {
      container: activeContainer,
      index: this.items[activeContainer].indexOf(active.id as string),
    };
  }

  @action.bound
  onDragOver(active: Active, over: Over | null): void {
    const overId = over?.id;
    if (overId == null || (active.id as string) in this.items) {
      return;
    }

    const overContainer = this.findItem(overId as string);
    const activeContainer = this.findItem(active.id as string);

    // Nothing enters or leaves the fixed section. dnd-kit's own `disabled` covers the pointer;
    // this covers the store, which the handlers reach through findItem regardless.
    if (this.isFixedSection(activeContainer) || this.isFixedSection(overContainer)) {
      return;
    }

    // Item dragged out of a layout section back to the Fields panel.
    if (!overContainer && overId === FIELD_CONTAINER_ID && activeContainer) {
      this.store.entityFields.addField(active.id as string);
      this.modifyItemsInContainer(
        activeContainer,
        this.items[activeContainer].filter((id) => id !== active.id) as string[]
      );
      return;
    }

    // Item dragged from the Fields panel into a layout section: insert at hover index.
    if (overContainer && !activeContainer) {
      this.store.entityFields.removeField(active.id as string);
      const items = this.items[overContainer];
      const overIndex = items.indexOf(overId as string);
      const insertAt = overIndex >= 0 ? overIndex : items.length;
      this.modifyItemsInContainer(overContainer, [
        ...items.slice(0, insertAt),
        active.id,
        ...items.slice(insertAt),
      ] as string[]);
      return;
    }

    if (!overContainer || !activeContainer) {
      return;
    }

    if (activeContainer !== overContainer) {
      this.moveItemsBetweenContainers(activeContainer, overContainer, overId as string, active, over as Over);
    } else {
      // Same-container hover: keep the items array in sync with the cursor so the placeholder
      // follows. Required for fields freshly inserted from the Fields panel — dnd-kit's
      // transform-based sorting only tracks items present in the SortableContext at drag start.
      const items = this.items[overContainer];
      const activeIndex = items.indexOf(active.id);
      const overIndex = items.indexOf(overId);
      if (activeIndex !== -1 && overIndex !== -1 && activeIndex !== overIndex) {
        this.modifyItemsInContainer(overContainer, arrayMove(items, activeIndex, overIndex) as string[]);
      }
    }
    this.store.entityFields.searchFields('');
  }

  @action.bound
  onDragEnd(active: Active, over: Over | null): void {
    if (over === null || over?.id === FIELD_CONTAINER_ID) {
      // Required fields can't be removed: surface a snackbar and reinsert at the original position.
      if (this.mustStayInLayout(active.id as string)) {
        this.store.notification.enqueueErrorSnackbar(
          this.lockedField(active.id as string)?.message ??
            'This is a required field and cannot be removed from the layout.'
        );
        // The field was visually moved into the Fields panel during drag — undo that, then put
        // it back in the saved initial container at the saved index.
        this.store.entityFields.removeField(active.id as string);
        const { container, index } = this.initialContainerInfo;
        if (container) {
          const items = this.items[container];
          if (!items.includes(active.id as string)) {
            this.modifyItemsInContainer(container, [
              ...items.slice(0, index),
              active.id,
              ...items.slice(index),
            ] as string[]);
          }
        }
        this.activeId = null;
        return;
      }
    }

    if ((active.id as string) in this.items && over?.id) {
      this.reorderSections(active.id.toString(), over.id.toString());
    }

    const activeContainer = this.findItem(active.id as string);
    if (active.id === FIELD_CONTAINER_ID || !activeContainer || over?.id == null) {
      this.activeId = null;
      return;
    }

    const overId = over.id;
    const overContainer = this.findItem(overId as string);
    if (this.isFixedSection(activeContainer) || this.isFixedSection(overContainer)) {
      this.activeId = null;
      return;
    }

    if (overContainer) {
      const activeIndex = this.items[activeContainer].indexOf(active.id);
      const overIndex = this.items[overContainer].indexOf(overId);
      if (activeIndex !== overIndex) {
        this.modifyItemsInContainer(
          overContainer,
          arrayMove(this.items[overContainer], activeIndex, overIndex) as string[]
        );
      }
    }

    this.activeId = null;
  }

  @action.bound
  onDragCancel(): void {
    if (this.clonedItems) {
      this.setItems(this.clonedItems);
    }
    this.activeId = null;
    this.clonedItems = null;
  }

  @action.bound
  transformDataForSave(): EntityLayoutType {
    const result: {
      type: string;
      order: string[];
      properties: EntityPropertiesType;
    } = {
      type: 'object',
      order: [],
      properties: {},
    };

    let newContainers = this.containers;

    _.forEach(this.items, (value, key) => {
      if (!newContainers.includes(key)) return;

      const sectionTitle = this.getSectionTitle(key);
      const newKey = this.derivedSectionKey(key);

      newContainers = newContainers.map((container) => (container === key ? newKey : container));

      _.set(result.properties, newKey, {
        type: 'object',
        order: value,
        title: sectionTitle,
      });
    });

    result.order = this.withFixedSectionLast(newContainers);

    return result;
  }
}
